import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'react-feather';
import { Badge, Button, Form, Modal, Table } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { getGroupMappingsAPI, getUsersListAPI, previewGroupExpansionAPI, updateGroupRulesAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import DrawerPanel from '../../../Components/shared/DrawerPanel';
import { companyUrl } from '../../../utils/mhNav';
import styles from './GroupMapping.module.scss';

export default function GroupMapping() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [drawer, setDrawer] = useState(null);
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [g, u] = await Promise.all([getGroupMappingsAPI(), getUsersListAPI()]);
      setRows(g.data || []);
      setUsers(u.data || []);
      setHasLoaded(true);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const userOptions = useMemo(
    () => users.map((x) => ({ value: x.id, label: `${x.displayName} (${x.email})` })),
    [users]
  );

  const openDrawer = (r) => {
    setDrawer(r);
    setDraft({
      ...r,
      excludeUserIds: r.excludeUserIds || [],
    });
  };

  const saveRules = async () => {
    try {
      await updateGroupRulesAPI(drawer.id, {
        autoExpand: draft.autoExpand,
        excludeRolePattern: draft.excludeRolePattern,
        excludeUserIds: draft.excludeUserIds,
        members: draft.members,
      });
      toast.success('Saved');
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const columns = useMemo(
    () => [
      { name: 'Group', selector: (r) => r.groupName, sortable: true },
      { name: 'Provider', cell: (r) => <Badge bg="secondary">{r.provider}</Badge>, width: '100px' },
      { name: 'Members', selector: (r) => r.memberCount, width: '90px' },
      {
        name: 'Auto expand',
        cell: (r) => (r.autoExpand ? 'Yes' : 'No'),
        width: '100px',
      },
      {
        name: 'Last sync',
        selector: (r) => new Date(r.lastSync).toLocaleString(),
        width: '160px',
      },
      {
        name: 'Actions',
        cell: (r) => (
          <Button size="sm" variant="outline-primary" onClick={() => openDrawer(r)}>
            Open
          </Button>
        ),
      },
    ],
    []
  );

  const toggleMemberInclude = (mid, val) => {
    const members = (draft.members || []).map((m) => (m.id === mid ? { ...m, includeAutoExpand: val } : m));
    setDraft({ ...draft, members });
  };

  if (loading && !hasLoaded) {
    return (
      <div>
        <PageHeader title="Group mapping" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Directory', path: companyUrl('directory') }, { label: 'Groups' }]} />
        <LoadingSkeleton rows={5} cols={4} />
      </div>
    );
  }

  if (error && !hasLoaded) {
    return (
      <div>
        <PageHeader title="Group mapping" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Directory', path: companyUrl('directory') }]} />
        <EmptyState icon={AlertTriangle} title="Error" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  const largeGroup = draft && draft.autoExpand && (draft.memberCount > 20 || (draft.members?.length || 0) > 20);

  return (
    <div>
      <PageHeader
        title="Group mapping"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Directory', path: companyUrl('directory') },
          { label: 'Groups' },
        ]}
      />

      {!rows.length ? (
        <EmptyState icon={AlertTriangle} title="No groups" description="Sync directory first." actionLabel="Reload" onAction={load} />
      ) : (
        <DataTableWrapper columns={columns} data={rows} loading={loading} showSearch keyField="id" onRowClick={openDrawer} />
      )}

      <DrawerPanel isOpen={Boolean(drawer)} onClose={() => setDrawer(null)} title={draft?.groupName || 'Group'} width={480}>
        {draft && (
          <>
            <p>
              <Badge bg="info">{draft.provider}</Badge>
            </p>
            {largeGroup && (
              <div className={`${styles.warnBanner} mb-3`}>
                Auto-inviting this group will invite many attendees — confirm before enabling.
              </div>
            )}
            <Form.Check
              type="switch"
              className="mb-3"
              label="Auto expand (auto-invite)"
              checked={!!draft.autoExpand}
              onChange={(e) => setDraft({ ...draft, autoExpand: e.target.checked })}
            />
            <Form.Group className="mb-3">
              <Form.Label>Exclude by role (pattern)</Form.Label>
              <Form.Control value={draft.excludeRolePattern || ''} onChange={(e) => setDraft({ ...draft, excludeRolePattern: e.target.value })} placeholder="e.g. intern" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Exclude specific users</Form.Label>
              <Select
                isMulti
                options={userOptions}
                value={userOptions.filter((o) => (draft.excludeUserIds || []).includes(o.value))}
                onChange={(opts) => setDraft({ ...draft, excludeUserIds: (opts || []).map((o) => o.value) })}
              />
            </Form.Group>
            <h3 className="h6">Members</h3>
            <Table responsive size="sm" className="small">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Include</th>
                </tr>
              </thead>
              <tbody>
                {(draft.members || []).slice(0, 15).map((m) => (
                  <tr key={m.id}>
                    <td>{m.name}</td>
                    <td>{m.email}</td>
                    <td>
                      <Form.Check checked={m.includeAutoExpand} onChange={(e) => toggleMemberInclude(m.id, e.target.checked)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="d-flex flex-wrap gap-2 mt-3">
              <Button
                variant="outline-secondary"
                onClick={async () => {
                  try {
                    const res = await previewGroupExpansionAPI(draft.id);
                    setPreview(res.data);
                  } catch (e) {
                    toast.error(e.message);
                  }
                }}
              >
                Preview expansion
              </Button>
              <Button onClick={saveRules}>Save rules</Button>
            </div>
          </>
        )}
      </DrawerPanel>

      <Modal show={Boolean(preview)} onHide={() => setPreview(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Expansion preview ({preview?.count} members)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ul className="small mb-0">
            {(preview?.members || []).map((m) => (
              <li key={m.id}>
                {m.name} — {m.email}
              </li>
            ))}
          </ul>
        </Modal.Body>
      </Modal>
    </div>
  );
}
